import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react"

import { cn } from "../lib/utils"
import { cva } from "class-variance-authority";
type SelectProps = React.ComponentProps<typeof SelectPrimitive.Root> & {
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
};
type SelectTriggerCustomProps = {
  isOpen?: boolean;
  hasValue?: boolean;
};

export const selectTriggerVariants = cva(
  "peer px-3 py-2 gap-2 flex w-full min-w-0 text-base cursor-pointer transition-colors shadow-xs outline-none md:text-sm file:hidden disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none border group",
  {
    variants: {
      variant: {
        outlined: "bg-transparent rounded-md border-border dark:border-border hover:border-border/80 dark:hover:border-border/80 hover:bg-card/30 dark:hover:bg-card/30 focus:border-border focus-visible:ring-0",
        standard: "border-0 border-b border-border dark:border-border bg-transparent hover:border-b-border dark:hover:border-b-border focus:border-b-border focus:border-b-[1px] focus-visible:ring-0 shadow-none",
        filled: "bg-muted/30 hover:bg-muted/50 dark:bg-card/30 rounded-md border-border dark:border-border hover:border-border/80 dark:hover:border-border/80 focus:border-border focus-visible:ring-0",
      },
      size: {
        sm: "h-8 text-sm",
        md: "h-10 text-sm",
        lg: "h-10 text-base",
      },
      state: {
        default: "",

        // only ring, no border override
        open: "ring-1 ring-[hsl(var(--border))]",

        // only ring, keep border as is
        filled: "ring-1 ring-[hsl(var(--border))]",

        error: "border-destructive focus-visible:border-destructive focus-visible:ring-secondary hover:border-destructive hover:border-[1px] focus:border-secondary focus:border-[1px]",

        disabled: "bg-bg-200 text-muted-foreground cursor-not-allowed opacity-60 ring-0 border-none hover:border-none",
      },
    },
    defaultVariants: {
      variant: "standard",
      size: "md",
      state: "default",
    },
  }
)

function Select({
  value: controlledValue,
  defaultValue,
  onValueChange,
  children,
  ...props
}: SelectProps & { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false)
  const isControlled = controlledValue !== undefined && onValueChange !== undefined
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue)
  const value = isControlled ? controlledValue : uncontrolledValue

  const handleValueChange = (val: string) => {
    if (!isControlled) setUncontrolledValue(val)
    onValueChange?.(val)
  }

  return (
    <SelectPrimitive.Root
      open={open}
      onOpenChange={setOpen}
      value={value}
      onValueChange={handleValueChange}
      {...props}
    >
      {React.Children.map(children, child => {
        if (React.isValidElement(child) && child.type === SelectTrigger) {
          return React.cloneElement(child as React.ReactElement<SelectTriggerCustomProps>, {
            isOpen: open,
            hasValue: !!value,
          })
        }
        return child
      })}
    </SelectPrimitive.Root>
  )
}

function SelectWrapper({
  label,
  description,
  error,
  children,
}: {
  label?: string;
  description?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
        </label>
      )}
      {description && (
        <p className="text-xs text-muted-foreground -mt-1 mb-1">
          {description}
        </p>
      )}
      {children}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

function SelectTrigger({
  className,
  variant = "outlined",
  size = "md",
  isInvalid = false,
  hasValue = false,
  disabled = false,
  isOpen = false,
  leftIcon,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  variant?: "outlined" | "standard" | "filled"
  size?: "sm" | "md" | "lg"
  isInvalid?: boolean
  hasValue?: boolean
  isOpen?: boolean
  leftIcon?: React.ReactNode
}) {
  const [keyboardFocus, setKeyboardFocus] = React.useState(false)
  const [lastInteractionWasKeyboard, setLastInteractionWasKeyboard] = React.useState(false)

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        setLastInteractionWasKeyboard(true)
      }
    }

    const handleMouseDown = () => {
      setLastInteractionWasKeyboard(false)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('mousedown', handleMouseDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('mousedown', handleMouseDown)
    }
  }, [])

  const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
    setKeyboardFocus(lastInteractionWasKeyboard)
    props.onFocus?.(e)
  }

  const handleBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
    setKeyboardFocus(false)
    props.onBlur?.(e)
  }

  const state = disabled
    ? "disabled"
    : isInvalid
      ? "error"
      : isOpen && hasValue
        ? "open"
        : hasValue
          ? "filled"
          : "default"

  return (
    <SelectPrimitive.Trigger
      disabled={disabled}
      aria-invalid={isInvalid}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={cn(
        "w-[400px]",
        selectTriggerVariants({ variant, size, state }),
        !isInvalid && isOpen && !keyboardFocus && variant !== "standard" && "ring-[1px] ring-[hsl(var(--border))]",
        !isInvalid && isOpen && !keyboardFocus && variant === "standard" && "border-b-[1px] border-b-border dark:border-b-border",
        isInvalid && isOpen && !keyboardFocus && variant !== "standard" && "ring-[1px] ring-destructive/20",
        isInvalid && isOpen && !keyboardFocus && variant === "standard" && "border-b-[1px] border-b-destructive",
        !isInvalid && keyboardFocus && variant !== "standard" && "ring-[3px] ring-[hsl(var(--border))]",
        !isInvalid && keyboardFocus && variant === "standard" && "!border !border-border !rounded-sm !ring-[3px] !ring-[hsl(var(--border))]",
        isInvalid && keyboardFocus && variant !== "standard" && "ring-[3px] ring-destructive/20",
        isInvalid && keyboardFocus && variant === "standard" && "!border !border-destructive !rounded-sm !ring-[3px] !ring-destructive/20",
        className
      )}
      {...props}
    >
      <div className="flex items-center gap-2 flex-1 overflow-hidden">
        {leftIcon && <span className="shrink-0 text-muted-foreground dark:text-muted-foreground/80 group-hover:text-foreground group-focus-within:text-primary transition-colors duration-200">{leftIcon}</span>}
        <span className="truncate flex-1 flex">{children}</span>
      </div>
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 opacity-50 text-muted-foreground dark:text-muted-foreground/80 group-hover:text-foreground group-focus-within:text-primary transition-colors duration-200" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          "bg-background text-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md",
          position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-2",
            position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  )
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "focus:bg-accent cursor-pointer focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-2 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 focus:outline-none focus:ring-0 focus:ring-transparent",
        className
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4 bg-[var(--checkboxBg)] " />
    </SelectPrimitive.ScrollDownButton>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  SelectWrapper
} 